import React from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_EVENT_COLOR } from '../../extensions/bundled/calendar/calendarStore';
import { eventRange, formatTime } from '../../utils/calendarDates';

/**
 * One event as a compact chip (Month cells, the +N overflow popover, Agenda
 * rows, the all-day row). A real <button> with a full accessible label so the
 * grid is keyboard/screen-reader usable (PLAN §3.5 a11y bar).
 */
export default function EventChip({ event, timeFormat, showTime = true, onSelect, onEventContextMenu }) {
  const { t } = useTranslation();
  const color = event.color || DEFAULT_EVENT_COLOR;
  const { start } = eventRange(event);
  const timeLabel = event.all_day ? t('calendar.allDay') : formatTime(start, timeFormat);
  const title = event.title || t('calendar.noTitle');
  const pending = Boolean(event.pending_invite);
  return (
    <button
      type="button"
      className={`cal-chip${event.status === 'cancelled' ? ' cal-chip--cancelled' : ''}${pending ? ' cal-chip--pending' : ''}`}
      aria-label={`${title}, ${timeLabel}${event.location ? `, ${event.location}` : ''}${pending ? `, ${t('calendar.chip.pendingInvitationSuffix')}` : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(event, e.currentTarget.getBoundingClientRect());
      }}
      // Right-click opens the event context menu (Outlook/Google standard;
      // fix round 4). stopPropagation keeps the day cell's own menu closed;
      // preventDefault only here, on the event surface.
      onContextMenu={(e) => {
        if (!onEventContextMenu) return;
        e.preventDefault();
        e.stopPropagation();
        onEventContextMenu(event, { x: e.clientX, y: e.clientY });
      }}
    >
      <span className="cal-chip-dot" style={{ background: color }} aria-hidden="true" />
      {showTime && !event.all_day && <span className="cal-chip-time">{timeLabel}</span>}
      <span className="cal-chip-title">{title}</span>
      {pending && <span className="cal-chip-badge">{t('calendar.chip.pending')}</span>}
    </button>
  );
}
